<template>
  <div class="search-block">
    <div class="divider top" />
    <div class="divider bottom" />
    <div class="container">
      <UiComponentHeading
        as="paragraph"
        :level="5"
      >
        <slot name="title" />
      </UiComponentHeading>
      <div class="search">
        <UiComponentInput
          id="search"
          ref="$input"
          :content="content"
          :placeholder="placeholder"
          name="search"
          size="l"
          type="text"
          @input:change="(searchFilter = $event), emit('input:change', $event)"
        />
        <UiComponentIcon type="search" />
        <UiComponentIcon
          v-if="searchFilter !== ''"
          type="x"
          @click="resetSearchFilter"
        />
        <AtomButton
          class="submit"
          :disabled="searchFilter === ''"
          to="#"
          @click="searchClicked"
        >
          <UiComponentIcon
            type="chevron-right"
            @click="resetSearchFilter"
          />
        </AtomButton>
      </div>
      <UiComponentSelect
        v-if="filterOptions.length > 0"
        id="filter"
        ref="selectFilter"
        :options="filterOptions"
        has-icon
        icon-type="filter"
        class="filter"
        size="l"
        @select:change="selectChange"
      />
    </div>
  </div>
</template>

<script setup>
// Emitter
const emit = defineEmits([
  'input:change',
  'search:click',
  'search:reset',
])

// Props
const props = defineProps({
  content: {
    default() {
      return undefined
    },
    required: false,
    type: String,
  },
  filterOptions: {
    default() {
      return []
    },
    required: false,
    type: Array,
  },
})

// Composables
const slots = useSlots()

// Refs
const $input = ref(null)
const searchFilter = ref('')
const sortBy = ref('new')

// Computed
const placeholder = computed(() => {
  const content = findContent(slots.placeholder() || [{ children: '' }])
  return content.replace(
    /\*(.*?)\*/gm,
    '<strong>$1</strong>',
  )
})

// Methods
function findContent(nodes) {
  const [{ children }] = nodes
  if (Array.isArray(children)) {
    return findContent(children)
  }
  else {
    return children
  }
}

function onKeyDown(event) {
  if (event.keyCode === 13) {
    searchClicked()
  }
}

function resetSearchFilter() {
  searchFilter.value = ''
  $input.value.reset()
  emit('search:reset', searchFilter.value)
}

function searchClicked() {
  emit('search:click', searchFilter.value)
}

function selectChange(value) {
  sortBy.value = value
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  $input.value.$el.removeEventListener('keydown', onKeyDown)
})

onMounted(() => {
  $input.value.$el.addEventListener('keydown', onKeyDown)
})
</script>

<style lang="postcss" scoped>
.search-block {
  @apply py-6 relative flex-wrap justify-between items-center overflow-x-hidden;

  @screen md {
    @apply flex overflow-x-visible;
  }

  @screen lg {
    @apply w-full;
  }

  @screen xl {
    @apply py-0;
  }

  .divider {
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    @apply absolute w-screen h-px bg-grey-blue-200;

    &.top {
      @apply top-0;
    }

    &.bottom {
      @apply bottom-0;
    }
  }

  > .container {
    @apply mx-auto flex flex-wrap items-center w-full;

    .h5 {
      @apply w-full flex-shrink-0 font-medium;

      @screen xl {
        @apply min-w-[290px] w-auto mr-10;
      }
    }

    .search {
      @apply mt-4 flex w-3/4 relative items-center flex-shrink-0 flex-grow;

      @screen md {
        @apply mt-6 w-auto;
      }

      @screen xl {
        @apply py-6 mt-0;
        max-width: 620px;
      }

      input {
        @apply pl-10 text-grey-blue-600;

        &::placeholder {
          @apply text-grey-blue-400;
        }
      }

      .icon-search {
        @apply absolute ml-4 text-grey-blue-400;
      }

      .icon-x {
        @apply absolute mr-4 text-grey-blue-600 z-10 cursor-pointer;
        right: calc(48px + 16px);
      }
    }

    .submit {
      @apply ml-4 flex items-center justify-center flex-shrink-0 w-12 h-12 bg-grey-blue-200 rounded;

      i {
        @apply text-grey-blue-600;
      }
    }

    .filter {
      @apply mt-6 ml-4 z-0 w-12 flex-shrink-0 bg-grey-blue-100;
      border-radius: 12px;

      @screen md {
        @apply ml-6 w-auto;
      }

      @screen xl {
        @apply mt-0;
      }

      :deep(.icon-chevron-down) {
        @apply hidden;

        @screen md {
          @apply flex;
        }
      }

      :deep(.icon-filter) {
        left: auto;

        @screen md {
          left: 12px;
        }
      }

      :deep(:is(.icon-filter, .icon-chevron-down)) {
        &::before {
          @apply text-sm;
        }
      }

      :deep(select) {
        @apply font-medium text-grey-blue-900 border-none;
      }
    }
  }
}
</style>
